import * as React from 'react';
import { Nav, Overlay, Popover, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './settings-announcement.css';

// Bump this id whenever a NEW announcement should re-surface for everyone who
// has already dismissed the previous one. The "New" badge reappears — and the
// popover opens itself on the next page load — for any user whose localStorage
// doesn't yet have this id marked as seen.
// Bumped when the August batch gained the previous-collection sections, the
// practitioner's own report sections and the menu reorganization: anyone who
// dismissed the first August note has not seen those.
// Bumped again for the release itself: the August note is rewritten around
// markers from any test, and this is the batch practitioners actually see, so
// it starts unseen for every account regardless of what any earlier id left
// behind in their browser.
const ANNOUNCEMENT_ID = 'whats-new-2026-09';

const ANNOUNCEMENT_TITLE = "What's New";
const ANNOUNCEMENT_INTRO = "We've been busy — here's what's new in LabSmarts:";

// A feature bullet. The object form leads with `name` in bold so the thing being
// announced is readable at a glance instead of buried mid-sentence — a list of
// full sentences all in one weight is what practitioners were skimming past.
// `desc` is optional: a bullet that doesn't split into name-plus-explanation is
// just a bold name.
//
// The plain-string form is what the earlier releases use and renders unchanged,
// so re-styling a new release never rewrites the ones already published.
type FeatureItem = string | { name: string; desc?: string; group?: string };

/**
 * Split a release's features into their `group` runs, in list order.
 *
 * A dozen bullets in one column is a wall a practitioner skims past, and the
 * ones they care about are usually all in one family — everything about their
 * own markers, or everything that changed on the report. Grouping is opt-in per
 * feature: releases whose features carry no group (every one before August
 * 2026) come back as a single unlabeled run and render exactly as they did.
 */
export function groupFeatures(
  features: FeatureItem[],
): { group?: string; items: FeatureItem[] }[] {
  const runs: { group?: string; items: FeatureItem[] }[] = [];
  (features || []).forEach(feature => {
    const group = typeof feature === 'string' ? undefined : feature.group;
    const last = runs[runs.length - 1];
    if (last && last.group === group) {
      last.items.push(feature);
    } else {
      runs.push({ group, items: [feature] });
    }
  });
  return runs;
}

// Running changelog, newest release first. Each release groups its features and
// setting groups under a release date. To announce a new batch, add a new entry
// at the TOP (keep the older ones below) and bump ANNOUNCEMENT_ID so the "New"
// badge re-surfaces and the note opens itself. `settings` names match the
// headers on the Report Settings page and the description explains what each controls.
const RELEASES: Array<{
  date: string;
  features: FeatureItem[];
  settings: Array<{ name: string; desc: string }>;
  // Optional "Read more" write-up for this specific release.
  blogUrl?: string;
}> = [
  {
    date: 'September 2026',
    features: [
      // HIGH-LEVEL FEATURES ONLY. This is a release note, not a changelog:
      // fixes and refinements (a note's arrows rendering properly, a cross-link
      // inside one marker's root causes) belong in the work, not here. A bullet
      // earns its place by being something a practitioner would go and try.
      // The group heading carries the framing on its own: "Add any marker, from
      // any test" already says the markers need not be blood work, and every
      // bullet under it is something to go and do. A "More than blood work"
      // headline bullet sat above them making the same point a second time in
      // the register of a marketing line, which is where that claim belongs.
      {
        group: 'Add any marker, from any test',
        name: 'Add your own markers to a test',
        desc:
          'in Add Test Results, for anything your lab measured that LabSmarts does not already carry',
      },
      {
        group: 'Add any marker, from any test',
        // The bullet above reads as "for the odd marker LabSmarts is missing",
        // and a practitioner who stops there never finds out that the marker
        // can go INTO a section rather than into an appendix at the end.
        // The two section names are examples introduced by "such as" and the
        // clause about sections you create follows them, so the pair reads as
        // an illustration of "any section" rather than as the only two on
        // offer. Naming none of them was the alternative, and an abstraction on
        // its own does not land in one read.
        name: 'File them into any section',
        desc:
          'a marker of your own can sit in a LabSmarts section, such as Hormone Balance or Kidney Function, or in any section that you create, charted against the ranges you set',
      },
      {
        group: 'Add any marker, from any test',
        // Numbers are what "marker" means everywhere else in LabSmarts, so a
        // urinalysis or a culture reads as out of scope until this says
        // otherwise. The expected wording is half the feature and belongs in
        // the same bullet: without it a text row is just a printed word.
        name: 'Results reported in words',
        desc:
          'a urinalysis "Negative" or a "Trace" prints on the report, and setting the expected wording makes anything else stand out',
      },
      {
        group: 'Add any marker, from any test',
        // Two examples that are nothing like each other, so the pair reads as
        // "anything" rather than "antibody panels": an IgG allergen panel and a
        // mycotoxin panel sat too close together to do that work. The point of
        // the bullet is the pairing with the one above it, so it ends on the
        // markers going into the section rather than on the naming.
        name: 'Add your own sections',
        desc:
          'a viral antigen panel, a mycotoxin panel: name the section yourself and file your own markers into it',
      },
      {
        // Named for both lists the page holds, since My Sections is half of
        // what is new here. The desc still points at the "My Markers" menu
        // item, which is what a practitioner will be looking for.
        group: 'Add any marker, from any test',
        name: 'My Markers & My Sections',
        desc:
          'your definitions saved once and reused on every test, on the My Markers page under the Test Results menu',
      },
      {
        group: 'Add any marker, from any test',
        // "Reused on every test" in the bullet above still means picking them
        // each time. This is the step past that, and it is the one that saves a
        // practitioner running the same panel on every client any typing at
        // all — so it earns a bullet rather than a clause.
        name: 'Add to every new test',
        desc:
          'tick a whole section or a single marker in My Markers and it is waiting on every test you start from then on',
      },
      // NOT listed here: "no limit on markers". Custom markers arrive in this
      // very release, so no practitioner has ever met a cap on them — announcing
      // the absence of one plants the idea that a limit was on the cards. Same
      // reason there is no bullet about a limit on sections.
      {
        group: 'Supplement protocols',
        name: 'Systemic Formulas protocols',
        desc: 'supplement recommendations matched to the patterns each section detects',
      },
      {
        group: 'Supplement protocols',
        name: 'Three views of the protocol',
        desc:
          'Top Priority, By System, or the full Roadmap, each with its own Include in PDF setting',
      },
      {
        group: 'Supplement protocols',
        // Dosing prints in exactly one place, and a practitioner who does not
        // know that goes looking for it on the section tabs.
        name: 'A client list with dosing',
        desc:
          'tick the products you want and the report prints a client-facing section with names, descriptions and dosing',
      },
      // NO GAUGE, NO PATTERN BOXES AND NO "NOT MEASURED YET" IN THIS GROUP.
      // All three were announced here once and none ships: the gauge was ruled
      // out by design (the marker IS the finding, so a likelihood word adds
      // nothing to "Vitamin D 22 ng/mL, Priority"), and the one-carbon pattern
      // boxes and the open questions are both held behind
      // nutrientPatternsVisible() until the "Nutrient Patterns" tab passes
      // clinical review. What the panel actually renders is the status tally
      // and the Focus list, so that is what these bullets describe.
      // The group heading matches the panel's own header, "Vitamin & Mineral
      // Insights", rather than the plural section name.
      {
        group: 'Vitamin & Mineral Insights',
        name: 'A new Insights panel',
        desc:
          'the section now opens with a count of its Priority, Support, Monitor and Optimal markers',
      },
      {
        group: 'Vitamin & Mineral Insights',
        name: 'A Focus list, worst first',
        desc:
          'every marker below optimal, grouped under its status with an arrow for the direction, on screen and in the client PDF',
      },
      {
        group: 'Stress & Adrenal Insights',
        // Was "the same panel shape", which pointed at the Vitamin & Mineral
        // bullets above. Those describe a panel with neither a gauge nor
        // pattern boxes, so the comparison now names Glucose directly.
        name: 'A likelihood gauge, then the patterns',
        desc:
          'the section opens the way Glucose does: the gauge names the condition and how likely it is, then a box for each pattern the adrenal markers point to',
      },
      {
        group: 'Stress & Adrenal Insights',
        // CORTISOL LEADS, unconditionally. Read adrenalGauge in
        // StressAdrenalAnalysis.ts, not the panel's info popover: the
        // 2026-08-23 change made cortisol's direction win outright
        // (`cortisolDir !== null ? cortisolDir : nakDir`), including when it
        // disagrees with a deviating Na/K, because reporting underactivity off
        // the ratio while the pattern box below showed a high cortisol was a
        // visible self-contradiction. Na/K is a weak aldosterone proxy that
        // also moves for non-adrenal reasons, so it only fills in when cortisol
        // is silent and is capped at 68% when it is the only deviating marker.
        // An earlier version of this bullet had the two the other way round.
        name: 'Cortisol sets the direction',
        desc:
          'the sodium/potassium ratio fills in when cortisol reads optimal or was not ordered, and on its own the ratio reports as supporting evidence rather than a verdict',
      },
      {
        group: 'Stress & Adrenal Insights',
        // "Reduced Stress Resilience" is the gauge's own label for the
        // 'reserve' condition, so the bullet uses those words exactly.
        name: 'DHEA-S and pregnenolone read the reserve',
        desc:
          'with cortisol and the ratio both optimal, a low reserve reads as reduced stress resilience, which shows before function itself slips',
      },
      {
        group: 'On the report',
        // NOT "Insights carry over": they only rebuild from the previous
        // collection when the section has no current results at all. A section
        // that WAS rerun keeps its own current Insights, so a bullet promising
        // carried-over Insights would be wrong most of the time. Framed as what
        // the practitioner can rely on instead: nothing drops off the report.
        name: 'Nothing disappears between tests',
        desc:
          'a panel the client did not repeat still shows its last results and findings, labeled with the date they were collected',
      },
      {
        group: 'On the report',
        // Bolded whole: this is the Results Overview's own branded name, so it
        // reads as one thing rather than a heading with a suffix.
        name: 'The Functional Blood Work Map™',
        desc:
          'the Results Overview now carries its own name, plus a marker count on every section',
      },
      {
        group: 'Around the app',
        name: 'Menus reorganized',
        desc:
          'Report Settings and My Markers moved to the Test Results menu, with "Prepared by" and your logo; Account now holds your Profile and subscription',
      },
      {
        group: 'Around the app',
        name: 'Redesigned login screens',
        desc: 'sign-in, sign-up and password',
      },
      {
        group: 'Around the app',
        name: 'Book a Clinical Review',
        desc: 'from the top menu',
      },
      {
        group: 'Around the app',
        name: 'Automatic sign-out after an hour',
        desc:
          'a warning appears five minutes ahead with a Stay signed in button, so client information is not left on an unattended screen; save your work when you see it',
      },
    ],
    // Name the OPTION that is new, not the setting group it sits under. Both
    // groups here shipped in June 2026, so listing a group by name reads as
    // "this setting is new" when it isn't:
    //   - PDF Inclusion is left out entirely. Every section on the report
    //     carries an "Include in PDF" checkbox by design, practitioner-named
    //     ones included, so a new section appearing in that list is the design
    //     working rather than a new setting.
    //   - Markers Outside Optimal is listed by its new "Minimize section"
    //     choice, alongside the show/collapse/hide choices it already had.
    settings: [
      {
        name: 'Report Titles',
        desc: 'the heading on each PDF cover page is yours to set, and no longer names blood work by default',
      },
      {
        name: 'Minimize Markers Outside Optimal section',
        desc: 'a new choice that collapses the section to its header on screen while still exporting it in full',
      },
      // Named as the whole group, which the note above warns against for
      // settings that already shipped. This one is the exception: supplements
      // were never visible in prod, so every option under this header is new
      // to the practitioners reading this.
      {
        name: 'Supplement Settings',
        desc: 'choose which supplement brands your reports draw from',
      },
    ],
  },
  {
    date: 'July 2026',
    features: [
      'Results Overview redesign',
      'About tab in every section for marker info',
      'Notes tab redesign with a consolidated Section Notes summary',
      'Estimated whole-blood viscosity calculation',
      'Raw data export with an AI prompt',
    ],
    settings: [
      {
        name: 'Test Comparison Bar Graphs',
        desc: 'also chart markers measured in the previous test but not the current one',
      },
    ],
  },
  {
    date: 'June 2026',
    features: [
      'Results Overview',
      'Markers Outside Optimal',
      'Glucose Dysregulation gauge & patterns',
      'Stress & Adrenal Balance section',
      'Floating Save button when adding & editing tests',
      'BMI bar graph',
    ],
    settings: [
      {
        name: 'PDF Inclusion',
        desc: 'choose which sections appear in the exported PDF',
      },
      {
        name: 'Markers Outside Optimal',
        desc: 'show, collapse, or hide the reference-range bar charts',
      },
      {
        name: 'Inflammation Section',
        desc: 'show only markers that signal here, or all of them',
      },
      {
        name: 'Immune Section',
        desc: 'control which immune markers and differential graphs appear',
      },
      {
        name: 'Blood Work Comparison Section Order',
        desc: 'match the analysis page order or the classic lab-report order',
      },
    ],
    blogUrl: 'https://labsmarts.com/new-labsmarts-features-june-2026/',
  },
];

// The nav label while this release is still unseen: "New in August" rather than
// the standing "What's New", because the standing label looks the same whether
// or not there is anything behind it and practitioners were reading past it.
// Derived from the newest release rather than typed, so it can never announce a
// month the changelog above has moved on from.
const unseenNavLabel = (): string => {
  const month = RELEASES[0]?.date.split(' ')[0];
  return month ? `New in ${month}` : ANNOUNCEMENT_TITLE;
};

interface OwnProps {
  // Namespaces the "seen" flag per practitioner so each account dismisses the
  // note independently (matching the existing per-profile localStorage pattern).
  userProfileId?: string;
}

type Props = OwnProps;

type State = {
  showNote: boolean;
  dismissed: boolean;
};

class SettingsAnnouncementBase extends React.Component<Props, State> {
  // Anchor for the controlled Overlay so the popover positions under the badge.
  private targetRef = React.createRef<HTMLDivElement>();

  // The auto-open fires at most once per mount. Without this, closing the
  // popover by clicking away (rootClose, which deliberately does NOT dismiss)
  // would leave `dismissed` false, and the next re-render would open it again —
  // a note the practitioner just closed would keep reappearing under them.
  private autoOpened = false;

  state: State = {
    showNote: false,
    dismissed: this.readDismissed(),
  };

  private storageKey(): string {
    return `${this.props.userProfileId}-announcement-${ANNOUNCEMENT_ID}`;
  }

  private readDismissed(): boolean {
    // Until the profile loads we don't know the user, so suppress the badge to
    // avoid flashing it for someone who already dismissed it. componentDidUpdate
    // re-reads the real value once userProfileId arrives.
    if (!this.props.userProfileId) {
      return true;
    }
    return localStorage.getItem(this.storageKey()) === 'true';
  }

  componentDidMount() {
    this.maybeAutoOpen();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.userProfileId !== this.props.userProfileId) {
      this.setState({ dismissed: this.readDismissed() });
    }
    this.maybeAutoOpen();
  }

  /**
   * Open the note by itself the first time a practitioner loads the app after a
   * new release, so a changelog nobody clicks still reaches them. "Got it" is
   * what stops it: it writes ANNOUNCEMENT_ID to localStorage, so the next bump
   * is the only thing that brings it back.
   *
   * Runs from didMount/didUpdate rather than the state initialiser because the
   * Overlay positions against `targetRef.current`, which is null during the
   * render that creates the anchor. Setting state here schedules one more
   * render, by which point the ref is attached and the popover lands under the
   * badge instead of in the corner of the page. The same beat also waits out
   * `userProfileId` arriving — until it does, render() returns null and
   * readDismissed() reports "dismissed" so the badge never flashes at someone
   * who already closed it.
   */
  private maybeAutoOpen(): void {
    if (this.autoOpened) return;
    if (!this.props.userProfileId) return;
    if (this.readDismissed()) return;

    this.autoOpened = true;
    this.setState({ showNote: true });
  }

  // Open/close the note without dismissing it — lets a user re-read it later.
  toggleNote = () => {
    this.setState(prev => ({ showNote: !prev.showNote }));
  };

  closeNote = () => {
    this.setState({ showNote: false });
  };

  // Permanently clears the "New" badge for this user (persists across reloads).
  dismiss = () => {
    if (this.props.userProfileId) {
      localStorage.setItem(this.storageKey(), 'true');
    }
    this.setState({ dismissed: true, showNote: false });
  };

  render() {
    // Wait for the profile so the dismissed state is keyed to the right user.
    if (!this.props.userProfileId) {
      return null;
    }

    const { showNote, dismissed } = this.state;

    return (
      <Nav.Item>
        <div
          ref={this.targetRef}
          className="nav-link"
          role="button"
          onClick={this.toggleNote}
          style={{
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            color: '#fff',
          }}
        >
          <FontAwesomeIcon icon="bell" className="ml-2 mr-2" />
          {/* Reverts to the standing label once dismissed, so the nav doesn't
              keep advertising a month the practitioner has already read. */}
          <span>{dismissed ? ANNOUNCEMENT_TITLE : unseenNavLabel()}</span>
          {!dismissed && (
            <Badge variant="danger" pill className="ml-1">
              New
            </Badge>
          )}
        </div>

        <Overlay
          target={this.targetRef.current}
          show={showNote}
          placement="bottom"
          rootClose
          onHide={this.closeNote}
        >
          <Popover
            id="settings-announcement-popover"
            className="whats-new-popover"
          >
            {/* Dismiss sits in the title row, not under the changelog: the note
                opens itself now, so the way to close it has to be visible the
                moment it appears rather than after a scroll through every
                release. Labeled rather than an "×" because this is permanent —
                it marks the release read — and an × reads as "close for now",
                which is what clicking away already does. */}
            <Popover.Title as="h3" className="whats-new-title">
              <span>{ANNOUNCEMENT_TITLE}</span>
              <Button
                variant="primary"
                size="sm"
                className="whats-new-dismiss"
                onClick={this.dismiss}
              >
                Got it
              </Button>
            </Popover.Title>
            <Popover.Content style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <p className="mb-2">{ANNOUNCEMENT_INTRO}</p>

              {RELEASES.map(release => (
                <div key={release.date} className="whats-new-release mb-3">
                  <div className="whats-new-release-date">
                    Release date: {release.date}
                  </div>

                  {release.features.length > 0 && (
                    <>
                      <div className="font-weight-bold mb-1">New features</div>
                      {groupFeatures(release.features).map((run, runIndex) => (
                        <React.Fragment key={run.group || `ungrouped-${runIndex}`}>
                          {run.group && (
                            <div className="whats-new-group">{run.group}</div>
                          )}
                          <ul className="whats-new-list mb-2">
                            {run.items.map(feature =>
                              typeof feature === 'string' ? (
                                <li key={feature}>{feature}</li>
                              ) : (
                                <li key={feature.name}>
                                  <strong>{feature.name}</strong>
                                  {feature.desc && <> &mdash; {feature.desc}</>}
                                </li>
                              ),
                            )}
                          </ul>
                        </React.Fragment>
                      ))}
                    </>
                  )}

                  {release.settings.length > 0 && (
                    <>
                      <div className="whats-new-settings-heading">New settings</div>
                      <ul className="whats-new-list mb-2">
                        {release.settings.map(setting => (
                          <li key={setting.name}>
                            <strong>{setting.name}</strong> &mdash; {setting.desc}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {release.blogUrl && (
                    <p className="whats-new-release-blog mb-0">
                      <a
                        href={release.blogUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Read more about the {release.date} release &rarr;
                      </a>
                    </p>
                  )}
                </div>
              ))}

            </Popover.Content>
          </Popover>
        </Overlay>
      </Nav.Item>
    );
  }
}

export const SettingsAnnouncement = SettingsAnnouncementBase;
